import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageDto } from '@asset-metrix/portal-gateway-client-js';
import { useShallow } from 'zustand/react/shallow';

import { useStore } from '../store';
import { getTableFilterInstanceId } from '../utils/filterInstanceId';
import { getAllTableWidgets } from '../utils/pageContentHelpers';

import { presetSelectionFromUrl } from './presetFilterSelectionForFilter';

export const TABLE_FILTER_URL_PARAM_PREFIX = 'tf_';

type ExtractedFilter = {
  [entityId: string]: {
    filterKey: string;
    searchParamKey: string;
    value: string[];
  }[];
};

export function createTableFilterSearchParamKey(
  widgetId: string,
  filterId: string
) {
  return `${TABLE_FILTER_URL_PARAM_PREFIX}${widgetId}_${filterId}`;
}

function extractFiltersFromUrlParam(
  existingFilters: ExtractedFilter,
  { key, value }: { key: string; value: string[] }
): ExtractedFilter {
  const [, entityId, ...filterKeySplit] = key.split('_');
  const filterKey = filterKeySplit.join('_');

  existingFilters[entityId] = [
    ...(existingFilters[entityId] || []),
    { filterKey, searchParamKey: key, value },
  ];
  return existingFilters;
}

export function usePresetTableFiltersFromUrl(
  pageConfig?: PageDto,
  pageFilterInstanceId?: string | null
) {
  const [searchParams] = useSearchParams();
  const [filterInitialized, presetFilterSelectionValue, setFilterInitialized] =
    useStore(
      useShallow((state) => [
        state.filterInitialized,
        state.presetFilterSelectionValue,
        state.setFilterInitialized,
      ])
    );

  const extractedFiltersPerEntity = useMemo(
    () =>
      [...searchParams.keys()]
        .filter((key) => key.startsWith(TABLE_FILTER_URL_PARAM_PREFIX))
        .map((key) => ({ key, value: searchParams.getAll(key) }))
        .reduce(extractFiltersFromUrlParam, {}),
    [searchParams]
  );

  useEffect(() => {
    if (!pageConfig || pageFilterInstanceId == null) {
      return;
    }

    for (const widget of getAllTableWidgets(pageConfig.contents)) {
      const tableFilterInstanceId = getTableFilterInstanceId(
        widget.id,
        pageFilterInstanceId
      );
      const tableConfig = widget.entity.config;

      if (!tableConfig || filterInitialized[tableFilterInstanceId]) {
        continue;
      }

      if (extractedFiltersPerEntity[widget.id]) {
        for (const extractedFilter of extractedFiltersPerEntity[widget.id]) {
          if (extractedFilter.value) {
            const filterConfig = tableConfig.filters?.find(
              (f) => f.key === extractedFilter.filterKey
            );

            if (
              filterConfig &&
              !(
                filterConfig.type === 'text' &&
                filterConfig.isGlobalSearchFilter
              )
            ) {
              presetSelectionFromUrl(
                presetFilterSelectionValue,
                tableFilterInstanceId,
                extractedFilter.value,
                filterConfig
              );
            }
          }
        }
      }

      setFilterInitialized(tableFilterInstanceId);
    }
  }, [
    pageConfig,
    pageFilterInstanceId,
    presetFilterSelectionValue,
    extractedFiltersPerEntity,
    setFilterInitialized,
    filterInitialized,
  ]);
}
